import classNames from 'classnames';
import { UIEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaMapMarkerAlt } from 'react-icons/fa';
import { RiArrowDownSFill, RiArrowRightSFill } from 'react-icons/ri';

import { BuildingWithAgreementSummary, useGetAgreementsQuery } from '@client/api';
import {
  IconButton,
  isDefinedNumber,
  isDefinedString,
  Pagination,
  Table,
  TableBody,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  Text,
  toMoneyStringOrUndefined,
  toNumberString,
} from '@client/toolkit';

import { AgreementListTableRow } from './agreement-list-row.component';

type AgreementBuildingItemProps =
  | {
      building: BuildingWithAgreementSummary;
      isFetching?: false;
      search?: string;
    }
  | {
      building?: BuildingWithAgreementSummary;
      isFetching: true;
      search?: string;
    };

export function AgreementBuildingItem({ building, isFetching, search }: AgreementBuildingItemProps) {
  const { t: tGlobal } = useTranslation('global');
  const { t: tType } = useTranslation('type');

  const [open, setOpen] = useState(false);

  // Auto-expand only if the building was matched by sub-level (agreement/tenant name),
  // not if it was matched by its own building name or address
  const isSubLevelMatch = useMemo(() => {
    if (!search || !building) return false;
    const s = search.toLowerCase();
    const nameMatch = building.buildingName?.toLowerCase().includes(s);
    const addressMatch = building.buildingAddress?.toLowerCase().includes(s);
    return !nameMatch && !addressMatch;
  }, [search, building]);

  useEffect(() => {
    setOpen(isSubLevelMatch);
  }, [isSubLevelMatch]);
  const hasAgreements = useMemo(() => (building?.agreementCount ?? 0) > 0, [building?.agreementCount]);
  const isExpandable = !!building?.buildingId;

  const pageSize = 20;
  const [page, setPage] = useState(0);
  const pageOffset = page * pageSize;

  const { data, isFetching: isFetchingAgreements } = useGetAgreementsQuery(
    {
      'buildingId.Equal': building?.buildingId,
      'pagination.Limit': pageSize,
      'pagination.Offset': pageOffset,
      search: isSubLevelMatch ? search : undefined,
    },
    { skip: !building?.buildingId || !open },
  );

  const agreements = useMemo(() => data?.items ?? [], [data?.items]);
  const totalCount = data?.totalCount ?? 0;

  const toggleOpen = useCallback(() => setOpen((currentOpen) => !currentOpen), []);
  const onArrowClick = useCallback(
    (event: UIEvent) => {
      toggleOpen();
      event.stopPropagation();
    },
    [toggleOpen],
  );

  const totalYearlyAmount = useMemo(() => {
    if (!building?.totalYearlyAmount) return undefined;
    return toMoneyStringOrUndefined({
      amount: building.totalYearlyAmount,
      currency: { code: building.currency ?? 'NOK' },
    });
  }, [building?.totalYearlyAmount, building?.currency]);

  return (
    <div className="border-t border-border">
      <div
        className={classNames(
          'relative flex flex-col items-stretch px-2 py-2',
          !isFetching ? 'cursor-pointer hover:bg-surface-hover transition ease-in-out' : undefined,
        )}
        onClick={isExpandable ? toggleOpen : undefined}
      >
        <div className="flex items-center justify-start">
          <IconButton
            color="primary"
            size="sm"
            fontSize="md"
            className={classNames('mr-2', !isExpandable && 'invisible opacity-0')}
            onClick={onArrowClick}
          >
            {open ? <RiArrowDownSFill /> : <RiArrowRightSFill />}
            <span className="sr-only">Show Agreements</span>
          </IconButton>

          <div className="relative overflow-hidden min-w-[300px]">
            <div className="flex gap-4 items-center">
              <Text
                as="div"
                className="text-sm leading-tight text-white font-bold whitespace-nowrap"
                loading={isFetching}
                isEmpty={!isDefinedString(building?.buildingName)}
                title={isDefinedString(building?.buildingName) ? building?.buildingName : undefined}
              >
                {building?.buildingName}
              </Text>
            </div>
            <div className="flex items-center overflow-hidden">
              <FaMapMarkerAlt className="text-xs text-red-600 inline mr-1" />
              <Text
                as="div"
                className="text-sm font-normal text-secondary flex-1 whitespace-nowrap"
                loading={isFetching}
                isEmpty={!isDefinedString(building?.buildingAddress)}
                title={isDefinedString(building?.buildingAddress) ? building?.buildingAddress : undefined}
              >
                {building?.buildingAddress}
              </Text>
            </div>
          </div>

          <div className="flex-1 flex items-center justify-end">
            <div className="flex items-center gap-4">
              <div className="px-3 py-2 text-left w-[120px]">
                <Text
                  as="div"
                  className="text-xs text-white whitespace-nowrap"
                  loading={isFetching}
                  isEmpty={!isDefinedNumber(building?.tenantCount)}
                >
                  {building?.tenantCount}
                </Text>
              </div>
              <div className="px-3 py-2 text-left w-[110px]">
                <Text
                  as="div"
                  className="text-xs text-white whitespace-nowrap"
                  loading={isFetching}
                  isEmpty={!isDefinedNumber(building?.agreementCount)}
                >
                  {building?.agreementCount}
                </Text>
              </div>
              <div className="px-3 py-2 text-left w-[145px]">
                <Text
                  as="div"
                  className="text-xs text-white whitespace-nowrap"
                  loading={isFetching}
                  isEmpty={!isDefinedNumber(building?.totalAreaMetric)}
                >
                  {toNumberString(building?.totalAreaMetric ?? undefined, undefined, {
                    unit: tType('AreaMeasurementUnit.SquareMeters', { context: 'short' }),
                  })}
                </Text>
              </div>
              <div className="px-3 py-2 text-left w-[120px]">
                <Text
                  as="div"
                  className="text-xs text-white whitespace-nowrap"
                  loading={isFetching}
                  isEmpty={!isDefinedString(totalYearlyAmount)}
                >
                  {totalYearlyAmount}
                </Text>
              </div>
            </div>
          </div>
        </div>
      </div>

      {open && (
        <>
          <Table className="w-full">
            <TableHead className="bg-stone-700">
              <TableHeadRow>
                <TableHeadCell>{tType('RelationshipType.Tenant')}</TableHeadCell>
                <TableHeadCell>{tType('RelationshipType.Owner')}</TableHeadCell>
                <TableHeadCell>{tGlobal('agreement.name')}</TableHeadCell>
                <TableHeadCell>{tGlobal('agreement.durationStart')}</TableHeadCell>
                <TableHeadCell>{tGlobal('agreement.durationEnd')}</TableHeadCell>
                <TableHeadCell>{tGlobal('agreement.totalYearlyAmount')}</TableHeadCell>
                <TableHeadCell>{tGlobal('agreement.status')}</TableHeadCell>
              </TableHeadRow>
            </TableHead>
            <TableBody>
              {isFetchingAgreements &&
                Array.from(Array(2).keys()).map((idx) => <AgreementListTableRow key={idx} isFetching />)}
              {!isFetchingAgreements &&
                agreements.map((agreement) => (
                  <AgreementListTableRow key={agreement.id} agreement={agreement} isFetching={false} />
                ))}
            </TableBody>
          </Table>
          <Pagination
            className="border-t"
            totalCount={totalCount}
            currentPage={page}
            setPage={setPage}
            pageSize={pageSize}
          />
        </>
      )}
    </div>
  );
}
